/**
 * What a gallery's covenant owes the artist, as the artist reads it: each
 * settlement grouped by whether it waits on their answer, is still owed, or
 * is closed, with the arithmetic that sized their share.
 *
 * Pure. The settlements and their thumbnails go in; a view comes out with
 * every figure and sentence decided. The copy is `collector.constants.ts`
 * and the read, the answer write and the dispute composer's open state are
 * `useArtistSettlementsController.ts`. The gallery's side of the same rows is
 * `gallery/settlements.model.ts`, and the two are not one file because they
 * disclose different things.
 *
 * The buyer is never shown, and `canAnswer` is true only while the gallery
 * says it paid and nothing has been said back — a second answer is refused in
 * Postgres, so offering the control again would be offering a 403.
 */

import type { ArtistSettlement, SettlementState } from "@ondo/shared";
import { percentOfBp, settlementState } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { money as sharedMoney } from "../lib/money.ts";
import type { Tile } from "../lib/Tiles.tsx";
import { formatConsignedDate } from "../gallery/gallery.model.ts";
import { ARTIST_SETTLEMENT_COPY } from "./collector.constants.ts";

/** One label/value pair of the arithmetic panel. */
export interface Figure {
  label: string;
  value: string;
  negative?: boolean;
  pending?: boolean;
}

/** One settlement as the artist reads it, with every label already decided. */
export interface ArtistSettlementRow {
  settlementId: string;
  workId: string;
  ondoCode: string;
  title: string;
  /** 'resold 10 Jul 2026'. The buyer is deliberately absent — see the model doc. */
  resale: string;
  /** 'Owed by Sadie Coles HQ (ONDO-1024)', or "" when the firm is unnamed. */
  owedBy: string;
  state: SettlementState;
  stateLabel: string;
  figures: Figure[];
  /** '£172,000', or an em dash while the gallery has not stated a rate. */
  owedToYou: string;
  note: string;
  /** True only while the gallery says it paid and the artist has not answered. */
  canAnswer: boolean;
  href: string;
  imageUrl: string | null;
}

export type ArtistSettlementsView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | { kind: "empty"; message: string; detail: string }
  | {
      kind: "ready";
      /** Remitted and unanswered. The only rows with a control. */
      toAnswer: ArtistSettlementRow[];
      /** Owed and not yet paid on. */
      owed: ArtistSettlementRow[];
      /** Answered, or nothing owed, or void. */
      closed: ArtistSettlementRow[];
      summary: string;
      /** Owed and recorded-as-paid, a pair per currency. See `describeTotals`. */
      totals: Tile[];
    };

/**
 * What one currency's settlements add up to.
 *
 * Minor units, so the arithmetic is exact; `describeTotals` renders them.
 */
export interface CurrencyTotal {
  currency: string;
  /** Computed shares not yet recorded as passed on: due and collected rows. */
  owedMinor: number;
  /** Shares the gallery has recorded paying, whatever the artist answered. */
  paidMinor: number;
  /** Rows counted into neither figure, each for a reason the note names. */
  rows: number;
  awaitingRate: number;
  nothingOwed: number;
}

/**
 * Everything the artist's screen renders, decided here.
 *
 * THE BUYER IS NEVER SHOWN, and that is a rule rather than an omission.
 * `ondo.artist_settlements()` does not return one: decision 3 of
 * `20260906090000_resale_settlements.sql` discloses the resale's buyer to the
 * COVENANT HOLDER because a disputed settlement is resolved by knowing the
 * parties, and that argument does not reach a party who is merely owed money
 * out of it. There is nothing here to render even if this file wanted to.
 *
 * `now` is accepted and unused today, kept for the reason `toSoldWorksView`
 * takes one: every screen on this rail resolves its dates against one clock,
 * and a screen that grows a countdown should not have to change shape to get it.
 */
export function toArtistSettlementsView(input: {
  settlements: ArtistSettlement[] | null;
  error: string | null;
  images: ReadonlyMap<string, string>;
  workBase: string;
}): ArtistSettlementsView {
  const { settlements, error, images, workBase } = input;

  if (error) {
    return { kind: "error", title: ARTIST_SETTLEMENT_COPY.errorTitle, message: error };
  }
  if (!settlements) return { kind: "loading", message: ARTIST_SETTLEMENT_COPY.loading };

  if (settlements.length === 0) {
    return {
      kind: "empty",
      message: ARTIST_SETTLEMENT_COPY.empty,
      detail: ARTIST_SETTLEMENT_COPY.emptyDetail,
    };
  }

  const rows = settlements.map((each) => toRow(each, workBase, images));

  return {
    kind: "ready",
    // WHAT IS WAITING ON THE READER, first. This is the whole reason the screen
    // exists — an artist who cannot answer is an artist whose gallery records
    // paying them unopposed.
    toAnswer: rows.filter((row) => row.canAnswer),
    owed: rows.filter((row) => !row.canAnswer && isOpen(row.state)),
    closed: rows.filter((row) => !row.canAnswer && !isOpen(row.state)),
    summary: ARTIST_SETTLEMENT_COPY.ledger(rows.length),
    totals: describeTotals(settlements),
  };
}

/**
 * The strip above the list: owed and recorded-as-paid, per currency — OND-70.
 *
 * NEVER SUMMED ACROSS CURRENCIES. Each settlement is denominated in its own
 * covenant's currency, so the only honest total is one per currency; a reader
 * with covenants in two sees two pairs, in code order, and no grand total.
 *
 * WHAT COUNTS AS MONEY. `owed` is a computed share the gallery has not yet
 * recorded passing on (`due` and `collected`); `paid` is one it has
 * (`settled`), whatever the artist said back — a disputed remittance is still
 * the gallery's own figure, and this strip is what galleries have recorded.
 * Three kinds of row are counted and NOT added, each for its own reason:
 * a row awaiting a rate has no figure yet, and adding it as zero would say a
 * resale made nothing before anybody has said what it made; a row owing
 * nothing (a loss, or a share that rounded to zero) IS a settlement and is
 * counted as one, but zero is not a sum; and a void row stopped being owed and
 * is left out of everything, including the count — it is a fact about a
 * withdrawn sale, not about this ledger.
 */
export function describeTotals(settlements: ArtistSettlement[]): Tile[] {
  return totalsByCurrency(settlements).flatMap((total) => {
    const parts = [ARTIST_SETTLEMENT_COPY.totals.rows(total.rows)];
    if (total.awaitingRate > 0) {
      parts.push(ARTIST_SETTLEMENT_COPY.totals.awaiting(total.awaitingRate));
    }
    if (total.nothingOwed > 0) {
      parts.push(ARTIST_SETTLEMENT_COPY.totals.nothing(total.nothingOwed));
    }
    const note = parts.join(" · ");

    return [
      {
        key: `owed-${total.currency}`,
        label: ARTIST_SETTLEMENT_COPY.totals.owed(total.currency),
        value: money(total.owedMinor, total.currency),
        note,
      },
      {
        key: `paid-${total.currency}`,
        label: ARTIST_SETTLEMENT_COPY.totals.paid(total.currency),
        value: money(total.paidMinor, total.currency),
        note: null,
      },
    ];
  });
}

/** The arithmetic behind `describeTotals`, kept separate so a test can read numbers. */
export function totalsByCurrency(settlements: ArtistSettlement[]): CurrencyTotal[] {
  const byCurrency = new Map<string, CurrencyTotal>();

  for (const each of settlements) {
    const state = settlementState(each);
    if (state === "void") continue;

    const total = byCurrency.get(each.currency) ?? {
      currency: each.currency,
      owedMinor: 0,
      paidMinor: 0,
      rows: 0,
      awaitingRate: 0,
      nothingOwed: 0,
    };
    total.rows += 1;

    switch (state) {
      case "awaiting_rate":
        total.awaitingRate += 1;
        break;
      case "nothing_owed":
        total.nothingOwed += 1;
        break;
      case "settled":
        total.paidMinor += each.artistMinor ?? 0;
        break;
      case "due":
      case "collected":
        total.owedMinor += each.artistMinor ?? 0;
        break;
    }

    byCurrency.set(each.currency, total);
  }

  // Code order, so two readers with the same covenants see the same strip and
  // a currency's pair never moves when a settlement lands.
  return [...byCurrency.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

function isOpen(state: SettlementState): boolean {
  return state === "due" || state === "awaiting_rate" || state === "collected";
}

function toRow(
  settlement: ArtistSettlement,
  workBase: string,
  images: ReadonlyMap<string, string>,
): ArtistSettlementRow {
  const state = settlementState(settlement);

  return {
    settlementId: settlement.settlementId,
    workId: settlement.workId,
    ondoCode: settlement.ondoCode,
    title: settlement.title,
    resale: `${ARTIST_SETTLEMENT_COPY.resoldOn} ${formatConsignedDate(settlement.resaleSoldAt)}`,
    owedBy: describeOwedBy(settlement),
    state,
    stateLabel: ARTIST_SETTLEMENT_COPY.state[state],
    figures: describeFigures(settlement),
    owedToYou: money(settlement.artistMinor, settlement.currency),
    note: describeNote(settlement, state),
    // ONLY while the gallery says it paid and nothing has been said back. A
    // void row is answerable by nobody, and an already-answered one is
    // append-once in Postgres — `assert_settlement_appended()` refuses a second
    // answer, so offering the control would be offering a 403.
    canAnswer:
      settlement.remittedAt !== null &&
      settlement.artistAnsweredAt === null &&
      settlement.voidedAt === null,
    href: `${workBase}/${settlement.workId}`,
    imageUrl: images.get(settlement.workId) ?? null,
  };
}

/**
 * '£172,000', or an em dash for a figure that does not exist yet.
 *
 * The rule lives in `lib/money.ts`; the gallery's settlements screen had a
 * byte-identical copy. This binds this screen's own placeholder copy.
 */
export function money(minor: number | null, currency: string): string {
  return sharedMoney(minor, currency, ARTIST_SETTLEMENT_COPY.pending);
}

/**
 * 'Owed by Sadie Coles HQ (ONDO-1024)'.
 *
 * The gallery is named because an artist cannot chase a payment from a party
 * they cannot identify — and it is NOT the gallery that sold the work on, which
 * the artist has no relation to at all.
 */
export function describeOwedBy(settlement: ArtistSettlement): string {
  if (!settlement.galleryName) return "";
  const id = settlement.galleryOndoId ? ` (${settlement.galleryOndoId})` : "";

  return `${ARTIST_SETTLEMENT_COPY.owedBy} ${settlement.galleryName}${id}`;
}

/**
 * The arithmetic, from the artist's side.
 *
 * FIVE CELLS, not the gallery's seven: there is no gallery split here, because
 * how the covenant holder divides its own half is not a fact about what the
 * artist is owed. Everything that PRODUCED their share is shown, including the
 * rate, because that is what a dispute is about.
 */
export function describeFigures(settlement: ArtistSettlement): Figure[] {
  const { currency, resaleCurrency } = settlement;
  const pending = settlement.shareMinor === null;

  const figures: Figure[] = [
    {
      label: ARTIST_SETTLEMENT_COPY.figures.original,
      value: money(settlement.originalMinor, currency),
    },
    {
      label: ARTIST_SETTLEMENT_COPY.figures.resale,
      value: money(settlement.resaleMinor, resaleCurrency),
    },
  ];

  if (resaleCurrency !== currency) {
    figures.push({
      label: ARTIST_SETTLEMENT_COPY.figures.converted(resaleCurrency, currency),
      value: money(settlement.resaleInCurrencyMinor, currency),
      pending,
    });
  }

  figures.push(
    {
      label: ARTIST_SETTLEMENT_COPY.figures.profit,
      value: money(settlement.profitMinor, currency),
      negative: settlement.profitMinor !== null && settlement.profitMinor < 0,
      pending,
    },
    {
      label: ARTIST_SETTLEMENT_COPY.figures.share(percentOfBp(settlement.shareBp)),
      value: money(settlement.shareMinor, currency),
      pending,
    },
    {
      label: ARTIST_SETTLEMENT_COPY.figures.yours(percentOfBp(settlement.artistSplitBp)),
      value: money(settlement.artistMinor, currency),
      pending,
    },
  );

  return figures;
}

/** The sentence under the panel. Every state answers; none is left blank. */
export function describeNote(
  settlement: ArtistSettlement,
  state: SettlementState,
): string {
  if (state === "void") return ARTIST_SETTLEMENT_COPY.notes.void;
  if (state === "awaiting_rate") {
    return ARTIST_SETTLEMENT_COPY.notes.awaitingRate(
      settlement.resaleCurrency,
      settlement.currency,
    );
  }
  if (settlement.artistAnsweredAt !== null) {
    const said = settlement.artistConfirmed
      ? ARTIST_SETTLEMENT_COPY.notes.confirmed
      : ARTIST_SETTLEMENT_COPY.notes.disputed;
    return settlement.artistNote ? `${said} ${settlement.artistNote}` : said;
  }
  if (settlement.remittedAt !== null) return ARTIST_SETTLEMENT_COPY.notes.remitted;
  if (settlement.collectedAt !== null) return ARTIST_SETTLEMENT_COPY.notes.collected;

  return ARTIST_SETTLEMENT_COPY.notes.due;
}

/**
 * What went wrong, in words an artist can act on.
 *
 * No 403 branch, unlike the gallery's: this route takes no id and requires no
 * membership. An artist with no settlements gets an empty list, not a refusal.
 */
export function describeArtistSettlementsError(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;

  return cause instanceof Error ? cause.message : ARTIST_SETTLEMENT_COPY.errorTitle;
}
